import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Check, Clock, ChefHat, Package, Truck, MapPin, Phone, MessageCircle, RefreshCw, Copy, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const orderSteps = [
  { id: 'received', label: 'Pedido recibido', icon: Check, description: 'Tu pedido fue confirmado' },
  { id: 'preparing', label: 'En preparación', icon: ChefHat, description: 'Nuestros chefs están cocinando' },
  { id: 'ready', label: 'Listo', icon: Package, description: 'Tu pedido está empacado' },
  { id: 'onway', label: 'En camino', icon: Truck, description: 'El repartidor va hacia ti' },
  { id: 'delivered', label: 'Entregado', icon: MapPin, description: '¡Buen provecho!' },
];

// Simulated driver info
const driverInfo = {
  name: 'Carlos M.',
  photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
  rating: 4.9,
  vehicle: 'Moto roja',
  phone: '81 9876 5432',
};

export default function OrderStatusPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const orderData = location.state?.orderData;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(25);

  // Simulate order progress
  useEffect(() => {
    const intervals = [
      setTimeout(() => setCurrentStepIndex(1), 3000),  // Preparing after 3s
      setTimeout(() => setCurrentStepIndex(2), 8000),  // Ready after 8s
      setTimeout(() => setCurrentStepIndex(3), 12000), // On way after 12s
      setTimeout(() => setCurrentStepIndex(4), 20000), // Delivered after 20s
    ];

    // Update estimated time
    const timeInterval = setInterval(() => {
      setEstimatedTime(prev => Math.max(0, prev - 1));
    }, 60000); // Every minute

    return () => {
      intervals.forEach(clearTimeout);
      clearInterval(timeInterval);
    };
  }, []);

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    toast.success('ID copiado al portapapeles');
  };

  const handleWhatsAppSupport = () => {
    window.open(`https://wa.me/528112345678?text=Hola, necesito ayuda con mi pedido ${orderId}`, '_blank');
  };

  // Fallback data if no state passed
  const order = orderData || {
    id: orderId,
    customerName: 'Cliente',
    total: 0,
    location: { name: 'Sucursal', address: 'Dirección' },
    deliveryType: 'delivery',
  };

  const isDelivered = currentStepIndex === 4;
  const showDriver = currentStepIndex >= 3 && order.deliveryType === 'delivery';

  return (
    <div className="min-h-screen pb-24 md:pb-12">
      {/* Header */}
      <div className="bg-accent text-accent-foreground py-8 md:py-12">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            {isDelivered ? (
              <Check className="w-8 h-8" />
            ) : (
              <Clock className="w-8 h-8" />
            )}
          </motion.div>
          <h1 className="font-heading text-2xl md:text-3xl font-medium">
            {isDelivered ? '¡Pedido entregado!' : 'Tu pedido está en camino'}
          </h1>
          <p className="mt-2 opacity-90">
            {isDelivered 
              ? 'Gracias por tu preferencia'
              : `Tiempo estimado: ${estimatedTime} min`
            }
          </p>
          
          {/* Order ID */}
          <button
            onClick={handleCopyOrderId}
            className="mt-4 inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm hover:bg-white/20 transition-colors"
            data-testid="copy-order-id"
          >
            <span>Pedido: {orderId}</span>
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Progress Timeline */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-6">
          <h2 className="font-medium mb-6">Estado del pedido</h2>
          
          <div className="relative">
            {orderSteps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;
              
              return (
                <div key={step.id} className="flex gap-4 pb-6 last:pb-0">
                  {/* Line */}
                  {index < orderSteps.length - 1 && (
                    <div 
                      className={`absolute left-5 w-0.5 h-12 mt-10 transition-colors duration-500 ${
                        isCompleted ? 'bg-accent' : 'bg-border'
                      }`}
                      style={{ top: `${index * 72}px` }}
                    />
                  )}
                  
                  {/* Icon */}
                  <motion.div
                    initial={false}
                    animate={{ 
                      scale: isCurrent ? [1, 1.1, 1] : 1,
                      backgroundColor: isCompleted ? 'hsl(var(--accent))' : 'hsl(var(--secondary))'
                    }}
                    transition={{ 
                      scale: { repeat: isCurrent ? Infinity : 0, duration: 2 },
                      backgroundColor: { duration: 0.3 }
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                      isCompleted ? 'text-accent-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    <StepIcon className="w-5 h-5" />
                  </motion.div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-1.5">
                    <p className={`font-medium ${isCompleted ? '' : 'text-muted-foreground'}`}>
                      {step.label}
                    </p>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                    {isCurrent && !isDelivered && (
                      <Badge className="mt-2 bg-accent/10 text-accent animate-pulse">
                        En proceso
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Driver info (when on delivery) */}
        {showDriver && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-6 mb-6"
          >
            <h2 className="font-medium mb-4">Tu repartidor</h2>
            <div className="flex items-center gap-4">
              <img
                src={driverInfo.photo}
                alt={driverInfo.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="flex-1">
                <p className="font-medium">{driverInfo.name}</p>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  {driverInfo.rating} · {driverInfo.vehicle}
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => window.open(`tel:${driverInfo.phone}`)}
                className="rounded-full"
                data-testid="call-driver-btn"
              >
                <Phone className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Delivery details */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-6">
          <h2 className="font-medium mb-4">Detalles de entrega</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-accent mt-0.5" />
              <div>
                <p className="font-medium">{order.location?.name}</p>
                <p className="text-muted-foreground">{order.location?.address}</p>
              </div>
            </div>
            {order.deliveryType === 'delivery' && order.address && (
              <div className="flex items-start gap-3 pt-3 border-t border-border/50">
                <Truck className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium">Entregar en:</p>
                  <p className="text-muted-foreground">{order.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Link to="/menu">
            <Button 
              variant="outline" 
              className="w-full h-12 rounded-full"
              data-testid="reorder-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reordenar
            </Button>
          </Link>
          <Button 
            onClick={handleWhatsAppSupport}
            className="w-full h-12 rounded-full bg-green-600 hover:bg-green-700"
            data-testid="whatsapp-support-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Soporte
          </Button>
        </div>

        {/* Rate order (when delivered) */}
        {isDelivered && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-secondary/30 rounded-2xl text-center"
          >
            <h3 className="font-medium mb-2">¿Cómo estuvo tu pedido?</h3>
            <p className="text-sm text-muted-foreground mb-4">Tu opinión nos ayuda a mejorar</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => toast.success('¡Gracias por tu calificación!')}
                  className="p-2 hover:scale-110 transition-transform"
                  data-testid={`rate-${rating}`}
                >
                  <Star className="w-8 h-8 text-yellow-500 hover:fill-yellow-500 transition-colors" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
